/**
 * 会话密钥加密工具
 * 使用 Web Crypto API 和派生密钥来加密存储在 localStorage 中的会话私钥
 */

import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("lib/utils/session-key-encryption");

export class SessionKeyEncryption {
	private static readonly SALT_KEY = "session_key_salt";
	private static readonly IV_KEY = "session_key_iv";
	private static readonly ENCRYPTED_KEY = "encrypted_session_key";
	private static readonly ITERATIONS = 100000;
	private static readonly KEY_LENGTH = 256;

	/**
	 * 从用户密码派生加密密钥
	 */
	private static async deriveKey(
		password: string,
		salt: Uint8Array,
	): Promise<CryptoKey> {
		const encoder = new TextEncoder();
		const passwordKey = await crypto.subtle.importKey(
			"raw",
			encoder.encode(password),
			"PBKDF2",
			false,
			["deriveKey"],
		);

		return crypto.subtle.deriveKey(
			{
				name: "PBKDF2",
				salt: salt.buffer as ArrayBuffer,
				iterations: SessionKeyEncryption.ITERATIONS,
				hash: "SHA-256",
			},
			passwordKey,
			{ name: "AES-GCM", length: SessionKeyEncryption.KEY_LENGTH },
			false,
			["encrypt", "decrypt"],
		);
	}

	/**
	 * 生成加密密码（新版本 - 更稳定）
	 * 使用用户的 principal 和稳定的浏览器指纹生成唯一密码
	 */
	private static async generatePassword(principal: string): Promise<string> {
		// 使用更稳定的浏览器指纹信息，避免因环境变化导致解密失败
		const fingerprint = [
			navigator.userAgent.split(" ")[0] || "", // 只取用户代理的第一部分，更稳定
			navigator.language,
			navigator.platform,
			// 移除了时区、屏幕尺寸等容易变化的属性
		].join("|");

		// 使用 principal 和指纹生成密码
		const data = new TextEncoder().encode(principal + fingerprint);
		const hashBuffer = await crypto.subtle.digest("SHA-256", data);
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
	}

	/**
	 * 生成加密密码（旧版本 - 用于兼容）
	 * 保留原来的指纹生成逻辑用于解密旧数据
	 */
	private static async generatePasswordLegacy(
		principal: string,
	): Promise<string> {
		// 原来的浏览器指纹信息（包含容易变化的属性）
		const fingerprint = [
			navigator.userAgent,
			navigator.language,
			navigator.platform,
			new Date().getTimezoneOffset().toString(),
			screen.colorDepth.toString(),
			screen.width.toString(),
			screen.height.toString(),
		].join("|");

		// 使用 principal 和指纹生成密码
		const data = new TextEncoder().encode(principal + fingerprint);
		const hashBuffer = await crypto.subtle.digest("SHA-256", data);
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
	}

	/**
	 * 加密会话私钥
	 */
	public static async encryptSessionKey(
		sessionPrivateKey: string,
		principal: string,
	): Promise<void> {
		try {
			// 生成盐值和 IV
			const salt = crypto.getRandomValues(new Uint8Array(16));
			const iv = crypto.getRandomValues(new Uint8Array(12));

			// 生成密码并派生密钥
			const password = await SessionKeyEncryption.generatePassword(principal);
			const key = await SessionKeyEncryption.deriveKey(password, salt);

			// 加密私钥
			const encoder = new TextEncoder();
			const encrypted = await crypto.subtle.encrypt(
				{
					name: "AES-GCM",
					iv: iv,
				},
				key,
				encoder.encode(sessionPrivateKey),
			);

			// 存储加密后的数据
			localStorage.setItem(
				SessionKeyEncryption.SALT_KEY,
				SessionKeyEncryption.arrayBufferToBase64(salt),
			);
			localStorage.setItem(
				SessionKeyEncryption.IV_KEY,
				SessionKeyEncryption.arrayBufferToBase64(iv),
			);
			localStorage.setItem(
				SessionKeyEncryption.ENCRYPTED_KEY,
				SessionKeyEncryption.arrayBufferToBase64(encrypted),
			);

			// 清除明文私钥（如果存在）
			localStorage.removeItem("user_session_private_key");
		} catch (error) {
			uiLog.error("加密会话私钥失败:", error);
			throw new Error("Failed to encrypt session key");
		}
	}

	/**
	 * 解密会话私钥（带兼容性回退）
	 */
	public static async decryptSessionKey(
		principal: string,
	): Promise<string | null> {
		// 获取存储的加密数据
		const saltBase64 = localStorage.getItem(SessionKeyEncryption.SALT_KEY);
		const ivBase64 = localStorage.getItem(SessionKeyEncryption.IV_KEY);
		const encryptedBase64 = localStorage.getItem(
			SessionKeyEncryption.ENCRYPTED_KEY,
		);

		if (!saltBase64 || !ivBase64 || !encryptedBase64) {
			return null;
		}

		// 转换为 ArrayBuffer
		const salt = SessionKeyEncryption.base64ToArrayBuffer(saltBase64);
		const iv = SessionKeyEncryption.base64ToArrayBuffer(ivBase64);
		const encrypted = SessionKeyEncryption.base64ToArrayBuffer(encryptedBase64);

		// 首先尝试使用新的密码生成方式
		try {
			const password = await SessionKeyEncryption.generatePassword(principal);
			const key = await SessionKeyEncryption.deriveKey(
				password,
				new Uint8Array(salt),
			);

			const decrypted = await crypto.subtle.decrypt(
				{
					name: "AES-GCM",
					iv: new Uint8Array(iv),
				},
				key,
				encrypted,
			);

			const decoder = new TextDecoder();
			return decoder.decode(decrypted);
		} catch (newError) {
			uiLog.warn("新版本解密失败，尝试旧版本兼容:", newError);

			// 如果新版本失败，尝试旧版本的密码生成方式
			try {
				const passwordLegacy =
					await SessionKeyEncryption.generatePasswordLegacy(principal);
				const keyLegacy = await SessionKeyEncryption.deriveKey(
					passwordLegacy,
					new Uint8Array(salt),
				);

				const decrypted = await crypto.subtle.decrypt(
					{
						name: "AES-GCM",
						iv: new Uint8Array(iv),
					},
					keyLegacy,
					encrypted,
				);

				const decoder = new TextDecoder();
				const result = decoder.decode(decrypted);

				uiLog.info("旧版本解密成功，将重新加密以使用新版本");
				// 解密成功后，用新版本重新加密以避免将来的问题
				setTimeout(async () => {
					try {
						await SessionKeyEncryption.encryptSessionKey(result, principal);
						uiLog.info("已升级到新版本加密");
					} catch (upgradeError) {
						uiLog.warn("升级加密版本失败:", upgradeError);
					}
				}, 100);

				return result;
			} catch (legacyError) {
				uiLog.error("解密会话私钥失败 (新旧版本均失败):", legacyError);
				return null;
			}
		}
	}

	/**
	 * 清除所有加密数据
	 */
	public static clearEncryptedData(): void {
		localStorage.removeItem(SessionKeyEncryption.SALT_KEY);
		localStorage.removeItem(SessionKeyEncryption.IV_KEY);
		localStorage.removeItem(SessionKeyEncryption.ENCRYPTED_KEY);
		localStorage.removeItem("user_session_private_key"); // 确保清除明文
	}

	/**
	 * 检查是否有加密的会话密钥
	 */
	public static hasEncryptedSessionKey(): boolean {
		return !!(
			localStorage.getItem(SessionKeyEncryption.SALT_KEY) &&
			localStorage.getItem(SessionKeyEncryption.IV_KEY) &&
			localStorage.getItem(SessionKeyEncryption.ENCRYPTED_KEY)
		);
	}

	/**
	 * 迁移旧的明文私钥到加密存储
	 */
	public static async migrateUnencryptedKey(
		principal: string,
	): Promise<boolean> {
		try {
			const unencryptedKey = localStorage.getItem("user_session_private_key");
			if (unencryptedKey && !SessionKeyEncryption.hasEncryptedSessionKey()) {
				await SessionKeyEncryption.encryptSessionKey(unencryptedKey, principal);
				return true;
			}
			return false;
		} catch (error) {
			uiLog.error("迁移未加密密钥失败:", error);
			return false;
		}
	}

	// 辅助方法：ArrayBuffer 转 Base64
	private static arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
		const bytes =
			buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
		let binary = "";
		for (let i = 0; i < bytes.byteLength; i++) {
			binary += String.fromCharCode(bytes[i]);
		}
		return btoa(binary);
	}

	// 辅助方法：Base64 转 ArrayBuffer
	private static base64ToArrayBuffer(base64: string): ArrayBuffer {
		const binary = atob(base64);
		const bytes = new Uint8Array(binary.length);
		for (let i = 0; i < binary.length; i++) {
			bytes[i] = binary.charCodeAt(i);
		}
		return bytes.buffer;
	}
}
